import { useState, useEffect } from "react";
import {
  Wrench, Smartphone, Battery, Monitor, Cpu, Clock, Shield, MapPin, Zap, Check, Star,
  PoundSterling, Calendar, Phone, Mail, Award, Lock, RefreshCw, Truck, Sparkles, Heart,
  Leaf, Camera, Pen, GraduationCap, ChevronDown, ArrowRight, CheckCircle2,
} from "lucide-react";

const ICONS = {
  wrench: Wrench, smartphone: Smartphone, battery: Battery, monitor: Monitor, cpu: Cpu, clock: Clock,
  shield: Shield, "map-pin": MapPin, zap: Zap, check: Check, star: Star, pound: PoundSterling,
  calendar: Calendar, phone: Phone, mail: Mail, award: Award, lock: Lock, refresh: RefreshCw,
  truck: Truck, sparkles: Sparkles, heart: Heart, leaf: Leaf, camera: Camera, pen: Pen, graduation: GraduationCap,
};
const Icon = ({ name, ...p }) => { const C = ICONS[name] || Sparkles; return <C {...p} />; };

function hexToRgb(h) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h || "");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [17, 24, 39];
}
function readableOn(bg) { const [r, g, b] = hexToRgb(bg); return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#111827" : "#ffffff"; }

function tokens(style, v) {
  const primary = v.primary || "#1f6feb";
  const accent = v.accent || v.secondary || primary;
  const heading = v.heading_font || "Poppins, Inter, sans-serif";
  const body = v.body_font || "Inter, system-ui, sans-serif";
  if (style === "slate") {
    return { primary, accent, heading, body, page: "#0d1117", panel: "#161b22", card: "#1c2230",
      cardBorder: "1px solid rgba(255,255,255,.08)", text: "#e6edf3", muted: "#9aa4b2", radius: "12px",
      heroBg: `radial-gradient(1200px 500px at 80% -10%, ${accent}33, transparent), #0d1117`,
      onPrimary: readableOn(primary), shadow: "0 10px 40px rgba(0,0,0,.4)", h1: 800, serif: false };
  }
  if (style === "warm") {
    return { primary, accent, heading: v.heading_font || "'Fraunces', Georgia, serif", body, page: "#fbf7f0",
      panel: "#f4ece0", card: "#ffffff", cardBorder: "1px solid rgba(0,0,0,.06)", text: "#2b2320", muted: "#6b5f55",
      radius: "18px", heroBg: `linear-gradient(160deg, #fbf7f0, ${accent}22)`, onPrimary: readableOn(primary),
      shadow: "0 12px 40px rgba(120,80,40,.12)", h1: 800, serif: true };
  }
  return { primary, accent, heading, body, page: "#ffffff", panel: "#f6f8fc", card: "#ffffff",
    cardBorder: "1px solid rgba(15,23,42,.08)", text: "#0f172a", muted: "#5b6472", radius: "20px",
    heroBg: `linear-gradient(135deg, ${primary}14, ${accent}22)`, onPrimary: readableOn(primary),
    shadow: "0 16px 48px rgba(2,6,23,.10)", h1: 800, serif: false };
}

const wrap = { maxWidth: 1120, margin: "0 auto", padding: "0 24px" };

export default function SiteRenderer({ site, assetUrl, onLead, preview }) {
  const c = site?.content || {};
  const v = site?.brand?.visual || {};
  const t = tokens(site?.style || "aurora", v);
  const [openFaq, setOpenFaq] = useState(0);
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => { const h = () => setVw(window.innerWidth); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);
  const mobile = vw < 640;
  const narrow = vw < 900;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent: false, website: "" });
  const [sent, setSent] = useState(false);
  const name = site?.name || "Your Business";
  const logo = site?.logo_asset_id && assetUrl ? assetUrl(site.logo_asset_id) : null;
  const hero = site?.hero_image_asset_id && assetUrl ? assetUrl(site.hero_image_asset_id) : null;

  const goContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  const Btn = ({ children, kind = "primary", onClick, href }) => {
    const st = kind === "primary"
      ? { background: t.primary, color: t.onPrimary, boxShadow: `0 8px 24px ${t.primary}55` }
      : { background: "transparent", color: t.serif ? t.text : t.primary, border: `2px solid ${t.primary}55` };
    const style = { ...st, padding: "14px 26px", borderRadius: 999, fontWeight: 700, fontSize: 16, cursor: "pointer",
      textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, border: st.border || "none" };
    return href ? <a href={href} target="_blank" rel="noopener noreferrer" style={style}>{children}</a>
      : <button onClick={onClick} style={style}>{children}</button>;
  };
  const H2 = ({ children }) => <h2 style={{ fontFamily: t.heading, fontWeight: t.h1, fontSize: "clamp(26px,3.4vw,38px)", color: t.text, letterSpacing: t.serif ? "0" : "-.02em", margin: 0 }}>{children}</h2>;
  const Section = ({ id, bg, children, label }) => (
    <section id={id} aria-label={label} style={{ background: bg || t.page, padding: "72px 0" }}>
      <div style={wrap}>{children}</div>
    </section>
  );

  const submit = async (e) => { e.preventDefault(); if (onLead) { const ok = await onLead(form); if (ok) setSent(true); } };

  const services = c.services || [];
  const whyUs = c.why_us || [];
  const process = c.process || [];
  const faqs = c.faqs || [];
  const hasTestimonials = Array.isArray(c.testimonials) && c.testimonials.length > 0;

  return (
    <div style={{ background: t.page, color: t.text, fontFamily: t.body, minHeight: "100vh", overflowX: "hidden" }} data-testid="site-renderer">
      {/* Header */}
      <header style={{ position: preview ? "static" : "sticky", top: 0, zIndex: 40, background: `${t.page}ee`, backdropFilter: "blur(10px)", borderBottom: t.cardBorder }}>
        <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, minHeight: 68, flexWrap: "wrap", paddingTop: mobile ? 12 : 0, paddingBottom: mobile ? 12 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {logo ? <img src={logo} alt={`${name} logo`} style={{ height: 38 }} loading="lazy" />
              : <span style={{ width: 34, height: 34, borderRadius: 9, background: t.primary, color: t.onPrimary, display: "grid", placeItems: "center", fontWeight: 800 }}>{(name[0] || "B").toUpperCase()}</span>}
            <span style={{ fontFamily: t.heading, fontWeight: 800, fontSize: 19, color: t.text }}>{name}</span>
          </div>
          <nav aria-label="Primary" style={{ display: "flex", gap: 22, alignItems: "center" }}>
            {!mobile && ["services", "process", "faq"].map((a) => (
              <a key={a} href={`#${a}`} style={{ color: t.muted, textDecoration: "none", fontSize: 15, textTransform: "capitalize" }}>{a}</a>
            ))}
            <Btn onClick={goContact}>{mobile ? "Get a quote" : (c.hero?.primary_cta || "Get a quote")}</Btn>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section aria-label="Introduction" style={{ background: t.heroBg, padding: mobile ? "56px 0 60px" : "84px 0 88px" }}>
        <div style={{ ...wrap, display: "grid", gridTemplateColumns: (hero && !narrow) ? "1.1fr .9fr" : "1fr", gap: 40, alignItems: "center" }}>
          <div style={{ maxWidth: (hero && !narrow) ? "none" : 760, margin: (hero && !narrow) ? 0 : "0 auto", textAlign: (hero && !narrow) ? "left" : "center" }}>
            {(c.hero?.badges || []).length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18, justifyContent: (hero && !narrow) ? "flex-start" : "center" }}>
                {c.hero.badges.map((b, i) => <span key={i} style={{ background: `${t.primary}22`, color: t.text, padding: "6px 12px", borderRadius: 999, fontSize: 13, fontWeight: 600 }}>{b}</span>)}
              </div>
            )}
            <h1 style={{ fontFamily: t.heading, fontWeight: t.h1, fontSize: "clamp(30px,5vw,54px)", lineHeight: 1.06, letterSpacing: t.serif ? "0" : "-.03em", color: t.text, margin: 0 }}>{c.hero?.headline || name}</h1>
            <p style={{ fontSize: "clamp(16px,2vw,20px)", color: t.muted, marginTop: 20, lineHeight: 1.55 }}>{c.hero?.subhead}</p>
            <div style={{ display: "flex", gap: 14, marginTop: 30, flexWrap: "wrap", justifyContent: (hero && !narrow) ? "flex-start" : "center" }}>
              <Btn onClick={goContact}>{c.hero?.primary_cta || "Get a quote"} <ArrowRight size={18} /></Btn>
              {c.hero?.secondary_cta && <Btn kind="ghost" onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}>{c.hero.secondary_cta}</Btn>}
            </div>
          </div>
          {hero && <div style={{ borderRadius: t.radius, overflow: "hidden", boxShadow: t.shadow, background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`, minHeight: narrow ? 220 : 320, aspectRatio: "4/3", maxWidth: "100%" }}><img src={hero} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} loading="eager" /></div>}
        </div>
      </section>

      {/* Trust bar */}
      {(c.trust_bar || []).length > 0 && (
        <div style={{ background: t.primary, color: t.onPrimary }}>
          <div style={{ ...wrap, display: "flex", flexWrap: "wrap", gap: 28, justifyContent: "center", padding: "16px 24px", fontWeight: 600 }}>
            {c.trust_bar.map((x, i) => <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><CheckCircle2 size={18} /> {x}</span>)}
          </div>
        </div>
      )}

      {/* Intro */}
      {c.intro?.heading && (
        <Section label="Overview">
          <div style={{ maxWidth: 780 }}>
            <H2>{c.intro.heading}</H2>
            <p style={{ color: t.muted, fontSize: 18, lineHeight: 1.7, marginTop: 16 }}>{c.intro.body}</p>
          </div>
        </Section>
      )}

      {/* Services */}
      {services.length > 0 && (
        <Section id="services" bg={t.panel} label="Services">
          <H2>What we do</H2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 20, marginTop: 34 }}>
            {services.map((s, i) => (
              <div key={i} style={{ background: t.card, border: t.cardBorder, borderRadius: t.radius, padding: 26, boxShadow: t.shadow }} data-testid={`svc-${i}`}>
                <span style={{ width: 48, height: 48, borderRadius: 12, background: `${t.primary}18`, color: t.primary, display: "grid", placeItems: "center", marginBottom: 16 }}><Icon name={s.icon} size={24} /></span>
                <h3 style={{ fontFamily: t.heading, fontWeight: 700, fontSize: 20, color: t.text, margin: 0 }}>{s.title}</h3>
                <p style={{ color: t.muted, marginTop: 10, lineHeight: 1.6 }}>{s.description}</p>
                {s.price_hint && <p style={{ color: t.primary, fontWeight: 700, marginTop: 14 }}>{s.price_hint}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Why us */}
      {whyUs.length > 0 && (
        <Section label="Why choose us">
          <H2>Why choose {name}?</H2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 22, marginTop: 34 }}>
            {whyUs.map((w, i) => (
              <div key={i} style={{ display: "flex", gap: 14 }}>
                <span style={{ color: t.primary, flexShrink: 0 }}><Icon name={w.icon || "check"} size={26} /></span>
                <div><h3 style={{ fontFamily: t.heading, fontWeight: 700, fontSize: 18, color: t.text, margin: 0 }}>{w.title}</h3><p style={{ color: t.muted, marginTop: 6, lineHeight: 1.6 }}>{w.description}</p></div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Process */}
      {process.length > 0 && (
        <Section id="process" bg={t.panel} label="How it works">
          <H2>How it works</H2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 20, marginTop: 34 }}>
            {process.map((p, i) => (
              <div key={i} style={{ background: t.card, border: t.cardBorder, borderRadius: t.radius, padding: 24 }}>
                <span style={{ fontFamily: t.heading, fontWeight: 800, fontSize: 34, color: t.primary }}>{String(i + 1).padStart(2, "0")}</span>
                <h3 style={{ fontFamily: t.heading, fontWeight: 700, fontSize: 18, color: t.text, marginTop: 6 }}>{p.title}</h3>
                <p style={{ color: t.muted, marginTop: 8, lineHeight: 1.6 }}>{p.description}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Coverage + hours */}
      {(c.coverage?.areas?.length > 0 || c.hours?.length > 0) && (
        <Section label="Coverage and hours">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 24 }}>
            {c.coverage?.areas?.length > 0 && (
              <div style={{ background: t.card, border: t.cardBorder, borderRadius: t.radius, padding: 26 }}>
                <h3 style={{ fontFamily: t.heading, fontWeight: 700, color: t.text, display: "flex", alignItems: "center", gap: 8 }}><MapPin size={20} color={t.primary} /> Areas we cover</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>{c.coverage.areas.map((a, i) => <span key={i} style={{ background: t.panel, padding: "6px 12px", borderRadius: 999, fontSize: 14, color: t.text }}>{a}</span>)}</div>
                {c.coverage.note && <p style={{ color: t.muted, marginTop: 12 }}>{c.coverage.note}</p>}
              </div>
            )}
            {c.hours?.length > 0 && (
              <div style={{ background: t.card, border: t.cardBorder, borderRadius: t.radius, padding: 26 }}>
                <h3 style={{ fontFamily: t.heading, fontWeight: 700, color: t.text, display: "flex", alignItems: "center", gap: 8 }}><Clock size={20} color={t.primary} /> Opening hours</h3>
                <div style={{ marginTop: 12 }}>{c.hours.map((h, i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: t.cardBorder, color: t.text }}><span>{h.day}</span><span style={{ color: t.muted }}>{h.time}</span></div>)}</div>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Guarantee / credentials / testimonials */}
      {(c.guarantee?.body || (c.credentials || []).length > 0 || hasTestimonials) && (
        <Section bg={t.panel} label="Trust">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 22 }}>
            {c.guarantee?.body && (
              <div style={{ background: t.primary, color: t.onPrimary, borderRadius: t.radius, padding: 30, boxShadow: t.shadow }}>
                <Shield size={30} /><h3 style={{ fontFamily: t.heading, fontWeight: 800, fontSize: 22, marginTop: 12 }}>{c.guarantee.title || "Our guarantee"}</h3><p style={{ marginTop: 10, lineHeight: 1.6, opacity: .95 }}>{c.guarantee.body}</p>
              </div>
            )}
            {(c.credentials || []).length > 0 && (
              <div style={{ background: t.card, border: t.cardBorder, borderRadius: t.radius, padding: 30 }}>
                <h3 style={{ fontFamily: t.heading, fontWeight: 700, color: t.text }}>How we work</h3>
                <ul style={{ marginTop: 14, listStyle: "none", padding: 0, display: "grid", gap: 10 }}>{c.credentials.map((cr, i) => <li key={i} style={{ display: "flex", gap: 10, color: t.text }}><Check size={20} color={t.primary} style={{ flexShrink: 0 }} /> {cr}</li>)}</ul>
              </div>
            )}
            {hasTestimonials && c.testimonials.map((tm, i) => (
              <div key={i} style={{ background: t.card, border: t.cardBorder, borderRadius: t.radius, padding: 30 }}>
                <p style={{ color: t.text, fontSize: 18, lineHeight: 1.6, fontStyle: "italic" }}>“{tm.quote}”</p><p style={{ color: t.muted, marginTop: 12, fontWeight: 600 }}>— {tm.author}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <Section id="faq" label="FAQs">
          <H2>Frequently asked questions</H2>
          <div style={{ marginTop: 28, maxWidth: 820 }}>
            {faqs.map((f, i) => (
              <div key={i} style={{ borderBottom: t.cardBorder }}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} aria-expanded={openFaq === i} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "18px 0", background: "none", border: "none", cursor: "pointer", textAlign: "left", color: t.text, fontFamily: t.heading, fontWeight: 700, fontSize: 17 }}>
                  {f.q} <ChevronDown size={20} style={{ transform: openFaq === i ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0, color: t.primary }} />
                </button>
                {openFaq === i && <p style={{ color: t.muted, paddingBottom: 18, lineHeight: 1.6 }}>{f.a}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* CTA band */}
      <section aria-label="Call to action" style={{ background: `linear-gradient(120deg, ${t.primary}, ${t.accent})`, color: t.onPrimary, padding: "72px 0", textAlign: "center" }}>
        <div style={wrap}>
          <h2 style={{ fontFamily: t.heading, fontWeight: 800, fontSize: "clamp(26px,3.5vw,40px)", margin: 0 }}>{c.cta_band?.headline || "Ready to get started?"}</h2>
          {c.cta_band?.subhead && <p style={{ marginTop: 14, fontSize: 18, opacity: .95, maxWidth: 620, margin: "14px auto 0" }}>{c.cta_band.subhead}</p>}
          <div style={{ marginTop: 28, display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={goContact} style={{ background: t.onPrimary, color: t.primary, padding: "15px 30px", borderRadius: 999, fontWeight: 800, fontSize: 16, border: "none", cursor: "pointer" }}>{c.cta_band?.cta || c.hero?.primary_cta || "Get a quote"}</button>
            {(site.booking_links || []).map((l) => <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer" style={{ background: "transparent", color: t.onPrimary, border: `2px solid ${t.onPrimary}`, padding: "13px 28px", borderRadius: 999, fontWeight: 700, textDecoration: "none" }}>{l.label}</a>)}
          </div>
        </div>
      </section>

      {/* Contact */}
      <Section id="contact" label="Contact">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 40 }}>
          <div>
            <H2>Get in touch</H2>
            <p style={{ color: t.muted, marginTop: 12, lineHeight: 1.6 }}>{c.contact?.note || "Send a message and we'll get back to you."}</p>
            <div style={{ marginTop: 20, display: "grid", gap: 12 }}>
              {c.contact?.email && <p style={{ color: t.text, display: "flex", gap: 10 }}><Mail size={18} color={t.primary} /> {c.contact.email}</p>}
              {c.contact?.phone && <p style={{ color: t.text, display: "flex", gap: 10 }}><Phone size={18} color={t.primary} /> {c.contact.phone}</p>}
            </div>
            {(site.payment_links || []).map((l) => <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", marginTop: 16, background: t.primary, color: t.onPrimary, padding: "12px 22px", borderRadius: 999, fontWeight: 700, textDecoration: "none" }}>{l.label}</a>)}
          </div>
          <div style={{ background: t.card, border: t.cardBorder, borderRadius: t.radius, padding: 28, boxShadow: t.shadow }}>
            {sent ? <p style={{ color: t.text, fontWeight: 600 }} data-testid="lead-sent">Thanks — your enquiry has been received.</p> : (
              <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
                {[["name", "Your name", "text"], ["email", "Email", "email"], ["phone", "Phone (optional)", "tel"]].map(([k, ph, ty]) => (
                  <label key={k} style={{ display: "grid", gap: 4 }}><span style={{ fontSize: 13, color: t.muted }}>{ph}</span>
                    <input required={k !== "phone"} type={ty} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`lead-${k}`} style={{ padding: 12, borderRadius: 10, border: t.cardBorder, background: t.page, color: t.text }} /></label>
                ))}
                <label style={{ display: "grid", gap: 4 }}><span style={{ fontSize: 13, color: t.muted }}>How can we help?</span>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="lead-message" style={{ padding: 12, borderRadius: 10, border: t.cardBorder, minHeight: 90, background: t.page, color: t.text }} /></label>
                <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={{ display: "none" }} />
                <label style={{ fontSize: 14, color: t.text, display: "flex", gap: 8 }}><input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="lead-consent" /> I consent to being contacted about my enquiry.</label>
                <button type="submit" data-testid="lead-submit" style={{ background: t.primary, color: t.onPrimary, padding: "14px", borderRadius: 999, fontWeight: 800, border: "none", cursor: "pointer", fontSize: 16 }}>Send enquiry</button>
              </form>
            )}
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer style={{ background: site?.style === "slate" ? "#080a0f" : t.text, color: "#fff", padding: "48px 0" }}>
        <div style={{ ...wrap, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 28 }}>
          <div><p style={{ fontFamily: t.heading, fontWeight: 800, fontSize: 20 }}>{name}</p><p style={{ opacity: .6, marginTop: 8, fontSize: 14 }}>{c.hero?.subhead?.slice(0, 90)}</p></div>
          <div><p style={{ fontWeight: 700, marginBottom: 10 }}>Explore</p>{["services", "process", "faq", "contact"].map((a) => <a key={a} href={`#${a}`} style={{ display: "block", color: "#fff", opacity: .7, textDecoration: "none", padding: "4px 0", textTransform: "capitalize", fontSize: 14 }}>{a}</a>)}</div>
          <div><p style={{ fontWeight: 700, marginBottom: 10 }}>Legal</p>{Object.entries(site?.legal || {}).map(([k, d]) => <span key={k} style={{ display: "block", opacity: .7, fontSize: 14, padding: "4px 0" }}>{d.title}</span>)}<p style={{ opacity: .65, fontSize: 11, marginTop: 8 }}>Legal pages are templates, not legal advice.</p></div>
          <div><p style={{ fontWeight: 700, marginBottom: 10 }}>Contact</p>{c.contact?.email && <p style={{ opacity: .7, fontSize: 14 }}>{c.contact.email}</p>}{c.contact?.phone && <p style={{ opacity: .7, fontSize: 14 }}>{c.contact.phone}</p>}</div>
        </div>
        <div style={{ ...wrap, borderTop: "1px solid rgba(255,255,255,.12)", marginTop: 28, paddingTop: 20, opacity: .7, fontSize: 13 }}>© {new Date().getFullYear()} {name}. Website built with The Honest Donkey.</div>
      </footer>
    </div>
  );
}
